import * as React from 'react'

import {
  BestSellers,
  BestSellersProps,
} from '@thg-commerce/enterprise-widget-best-sellers'

import {
  GlobalFourBestSellers,
  GlobalProductCardScroller,
  ProductVariant,
} from '@thg-commerce/enterprise-network/src/generated/graphql'
import { i18n } from '@thg-commerce/enterprise-core'
import { spacing, styled } from '@thg-commerce/enterprise-theme'

import { BaseWidgetProps } from '../types'

export interface ProductVariantItem
  extends Omit<
    ProductVariant,
    'product' | 'choices' | 'content' | 'inStock' | 'availabilityMessage'
  > {
  product: {
    reviews?: {
      averageScore: number
      total: number
      __typename: 'ProductReviews'
    }
    url: string
    __typename: 'Product'
  }
}

export type GlobalProductCardScrollerProps = BaseWidgetProps &
  Omit<GlobalProductCardScroller, 'query' | 'productSkuList'> & {
    productSkuList?: ProductVariantItem[]
  }

export interface BestSellersRendererStandardProps
  extends Omit<
    GlobalFourBestSellers & BaseWidgetProps,
    'ProductOne' | 'ProductTwo' | 'ProductThree' | 'ProductFour' | 'query'
  > {
  ProductOne: ProductVariantItem
  ProductTwo: ProductVariantItem
  ProductThree: ProductVariantItem
  ProductFour: ProductVariantItem
}

export type BestSellersRendererProps =
  | BestSellersRendererStandardProps
  | GlobalProductCardScrollerProps

export const getProductProps = (productVariant: ProductVariantItem) => {
  return (
    productVariant && {
      title: { value: productVariant.title },
      url: productVariant.product.url,
      sku: productVariant.sku,
      image: {
        urls: {
          largeProduct:
            (productVariant.images?.length > 0 &&
              productVariant.images[0].largeProduct) ||
            '',
          ...productVariant.images[0],
        },
        altText: '',
        isAmp: false,
      },
      review: productVariant.product.reviews && {
        starRating: productVariant.product.reviews.averageScore,
        numberOfReviews: productVariant.product.reviews.total,
      },
    }
  )
}

const StyledBestSellers = styled(BestSellers)`
  margin: ${spacing(2)} auto;
  padding: 0 ${spacing(3)};
  max-width: ${(props) => props.theme.site.siteWidth};
`

export const BestSellersWidgetRenderer = (props: BestSellersRendererProps) => {
  const i18nText = {
    title: i18n('general.productbuynow.text'),
    ariaLabel: i18n('general.productbuynow.text'),
    quickBuyTitle: i18n('general.product.quickbuy.text'),
  }

  if (props.__typename === 'GlobalFourBestSellers') {
    const bestSellersProps: BestSellersProps = {
      title: props.Title ? { text: props.Title } : undefined,
      productBlocks: [
        {
          ...getProductProps(props.ProductOne),
        },
        {
          ...getProductProps(props.ProductTwo),
        },
        {
          ...getProductProps(props.ProductThree),
        },
        {
          ...getProductProps(props.ProductFour),
        },
      ],
      shouldDisplayTitle: !props.disableTitle || props.disableTitle === 'false',
    }

    return <StyledBestSellers {...bestSellersProps} />
  }

  if (
    props.__typename === 'GlobalProductCardScroller' &&
    props.productSkuList
  ) {
    const globalProductCardScrollerProps: BestSellersProps = {
      productBlocks: props.productSkuList.map((product) => {
        return {
          ...getProductProps(product),
          button: i18nText,
        }
      }),
      title: (props.title && { text: props.title }) || undefined,
    }

    return <StyledBestSellers {...globalProductCardScrollerProps} />
  }

  return null
}
